import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { isChunkLoadError } from '../utils/lazyWithRetry';
import { branding } from '../config/branding';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  secondsLeft: number;
}

const REDIRECT_SECONDS = 15;

class ErrorBoundary extends Component<Props, State> {
  private redirectTimer: ReturnType<typeof setInterval> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      secondsLeft: REDIRECT_SECONDS,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
    if (isChunkLoadError(error) && !sessionStorage.getItem('chunk-reload-attempt')) {
      sessionStorage.setItem('chunk-reload-attempt', '1');
      const url = new URL(window.location.href);
      url.searchParams.set('_cb', String(Date.now()));
      window.location.replace(url.toString());
      return;
    }
    this.startRedirectCountdown();
  }

  componentWillUnmount() {
    this.clearRedirectCountdown();
  }

  startRedirectCountdown() {
    this.clearRedirectCountdown();
    this.redirectTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.secondsLeft <= 1) {
          this.clearRedirectCountdown();
          window.location.replace('/player/home');
          return { ...prev, secondsLeft: 0 };
        }
        return { ...prev, secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);
  }

  clearRedirectCountdown() {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
      this.redirectTimer = null;
    }
  }

  handleReload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(Date.now()));
    window.location.replace(url.toString());
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen bg-white font-sans selection:bg-black selection:text-white flex flex-col antialiased">
          {/* Main Content */}
          <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-10">
            <div className="max-w-md w-full">
              <div className="rounded-3xl bg-white border border-gray-200/70 shadow-sm p-8 sm:p-10 text-center">
                {/* Icon tile */}
                <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-5">
                  <AlertCircle className="w-5 h-5" />
                </div>

                <h1 className="text-2xl font-black tracking-tight text-gray-900 mb-2">
                  {chunkError ? 'App update required' : 'Something went wrong'}
                </h1>
                <p className="text-sm text-gray-400 leading-relaxed mb-1">
                  {chunkError
                    ? 'A new version was deployed. Reload to fetch the latest code.'
                    : this.state.error?.message || 'An unexpected error occurred.'}
                </p>
                {!chunkError && (
                  <p className="text-xs text-gray-400 mb-6">
                    Redirecting to your home page in{' '}
                    <span className="font-bold text-gray-900 tabular-nums">{this.state.secondsLeft}</span>{' '}
                    second{this.state.secondsLeft === 1 ? '' : 's'}…
                  </p>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={this.handleReload}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:bg-black transition-colors shadow-lg shadow-gray-900/10"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {chunkError ? 'Reload now' : 'Reload Page'}
                  </button>
                  {!chunkError && (
                    <a
                      href="/player/home"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <Home className="w-4 h-4" />
                      Go Home
                    </a>
                  )}
                </div>

                {!chunkError && this.state.errorInfo && (
                  <details className="mt-6 text-left">
                    <summary className="cursor-pointer text-xs font-bold text-gray-400 hover:text-gray-900 transition-colors">
                      Error details
                    </summary>
                    <pre className="mt-2 p-4 bg-gray-50 border border-gray-200/70 rounded-2xl overflow-auto text-xs text-gray-500">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-gray-200/60 py-8">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src={branding.logos.main} alt="Logo" className="h-5 w-auto opacity-40" />
                <span className="text-sm text-gray-400">ChessDirector</span>
              </div>
              <p className="text-sm text-gray-400">© {new Date().getFullYear()}</p>
            </div>
          </footer>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
